import React, { useMemo, useState } from "react";
import { message } from "antd";
import Select from "react-select";
import {
  CreditCard,
  X,
  ChevronUp,
  ChevronDown,
  Trash2,
  Phone,
  User,
  Book as Receipt,
  Gift,
  Home,
  Percent,
  Printer,
  DollarSign,
  FileText,
  Plus,
  Shield,
} from "react-feather";
import Swal from "sweetalert2";
import axios from "axios";
import moment from "moment";
import { useNotification } from "../../../../context/NotificationContext";

const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];

const generateUUID = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });

const getBusinessDate = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const value = localStorage.getItem(key);
    if (value && moment(value).isValid()) {
      return moment(value).format("YYYY-MM-DD");
    }
  }
  return moment().format("YYYY-MM-DD");
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const asMoney = (value, currency = "₹") => {
  const numberValue = toNumber(value);
  return `${currency}${numberValue.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const toAmount = (item) =>
  toNumber(item?.amount ?? toNumber(item?.qty) * toNumber(item?.price_standard));

const getItemTaxBreakdown = (item) => {
  if (Array.isArray(item?.tax_breakdown) && item.tax_breakdown.length > 0) {
    return item.tax_breakdown.map((tax) => ({
      tax_id: tax.tax_id || tax.id || tax.name || "unknown",
      name: tax.name || tax.tax_name || "Tax",
      rate: toNumber(tax.rate ?? tax.tax_rate),
    }));
  }
  if (Array.isArray(item?.taxes) && item.taxes.length > 0) {
    return item.taxes.map((tax) => ({
      tax_id: tax.tax_id || tax.id || tax.name || "unknown",
      name: tax.name || tax.tax_name || "Tax",
      rate: toNumber(tax.rate ?? tax.tax_rate),
    }));
  }
  const fallbackRate = toNumber(item?.total_tax_rate ?? item?.tax_rate);
  if (fallbackRate > 0) {
    return [{ tax_id: item?.tax_id || "unknown", name: item?.tax_name || "Tax", rate: fallbackRate }];
  }
  return [];
};

const getTotalTaxRate = (item, breakdown) => {
  const directRate = toNumber(item?.total_tax_rate);
  if (directRate > 0) return directRate;
  return (breakdown || []).reduce((sum, tax) => sum + toNumber(tax.rate), 0);
};

const normalizeOptionArray = (arr) => (Array.isArray(arr) ? arr : []);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 14,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.12)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    fontSize: 13,
    fontWeight: 700,
    "&:hover": { borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.22)" },
  }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontWeight: 700 }),
  singleValue: (base) => ({ ...base, color: "#0f172a", fontWeight: 850 }),
  menuPortal: (base) => ({ ...base, zIndex: 2147483647 }),
  menu: (base) => ({ ...base, zIndex: 2147483647, borderRadius: 14, overflow: "hidden" }),
  option: (base, state) => ({ ...base, fontSize: 13, fontWeight: state.isSelected ? 850 : 700, cursor: "pointer" }),
};

const Toggle = ({ id, checked, onChange, label, icon, disabled = false }) => (
  <label className={`ns-bill-toggle ${checked ? "active" : ""} ${disabled ? "disabled" : ""}`} htmlFor={id}>
    <input id={id} type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
    <span className="ns-bill-toggle-track" />
    <span className="ns-bill-toggle-copy">
      {icon}
      {label}
    </span>
  </label>
);

const BillGeneration = ({
  isOpen,
  onClose,
  entityId,
  entityType,
  kotItems = [],
  kotCount = 0,
  paymentOptions = [],
  sourceOptions = [],
  settings,
  roomOptions = [],
  fetchData,
  tableNo,
  apiUrl,
  token,
  setKotItems,
}) => {
  const defaultSource =
    entityType === "room"
      ? { value: "Room Service", label: "Room Service" }
      : { value: "Dine In", label: "Dine In" };

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMode, setPaymentMode] = useState(null);
  const [taxIncluded, setTaxIncluded] = useState(true);
  const [orderSource, setOrderSource] = useState(defaultSource);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isFooterExpanded, setIsFooterExpanded] = useState(false);
  const [isPartialPaymentEnabled, setIsPartialPaymentEnabled] = useState(false);
  const [partialPayments, setPartialPayments] = useState([]);
  const [currentPartialMethod, setCurrentPartialMethod] = useState("Cash");
  const [currentPartialAmount, setCurrentPartialAmount] = useState("");
  const [isComplementary, setIsComplementary] = useState(false);
  const [isRoomCredit, setIsRoomCredit] = useState(false);
  const [selectedCreditRoom, setSelectedCreditRoom] = useState(
    entityType === "room" ? { value: entityId, label: entityId } : null
  );
  const [isSaving, setIsSaving] = useState(false);

  // ── printIframeRef REMOVED — printing handled by kitchen agent via Socket.IO
  const currencyCode = localStorage.getItem("currencycode") || "₹";

  const safePaymentOptions = normalizeOptionArray(paymentOptions);
  const safeSourceOptions  = normalizeOptionArray(sourceOptions);
  const safeRoomOptions    = normalizeOptionArray(roomOptions);
  const safeKotItems       = normalizeOptionArray(kotItems);
  const { notify } = useNotification();

  const calculation = useMemo(() => {
    const taxMap = {};
    let exclusiveAdditionalTax = 0;

    const normalizedItems = safeKotItems.map((item) => {
      const amount = toAmount(item);
      const breakdown = getItemTaxBreakdown(item);
      const totalTaxRate = getTotalTaxRate(item, breakdown);
      const isExclusive = Boolean(item?.is_price_exclusive_tax);
      const isTaxable = Boolean(item?.is_taxable) && breakdown.length > 0;

      let baseAmount = amount;
      let itemTaxTotal = 0;

      if (isTaxable) {
        if (isExclusive) {
          baseAmount = amount;
          itemTaxTotal = breakdown.reduce((sum, tax) => {
            const taxRate = toNumber(tax.rate);
            const taxAmount = (amount * taxRate) / 100;
            exclusiveAdditionalTax += taxAmount;
            const taxId = tax.tax_id || tax.name || "unknown";
            if (!taxMap[taxId]) {
              taxMap[taxId] = { tax_id: taxId, tax_name: tax.name || "Tax", tax_rate: taxRate, amount: 0 };
            }
            taxMap[taxId].amount += taxAmount;
            return sum + taxAmount;
          }, 0);
        } else if (totalTaxRate > 0) {
          baseAmount = amount / (1 + totalTaxRate / 100);
          const totalTaxAmount = amount - baseAmount;
          itemTaxTotal = totalTaxAmount;
          breakdown.forEach((tax) => {
            const taxRate = toNumber(tax.rate);
            const taxAmount = totalTaxAmount * (taxRate / totalTaxRate);
            const taxId = tax.tax_id || tax.name || "unknown";
            if (!taxMap[taxId]) {
              taxMap[taxId] = { tax_id: taxId, tax_name: tax.name || "Tax", tax_rate: taxRate, amount: 0 };
            }
            taxMap[taxId].amount += taxAmount;
          });
        }
      }

      return {
        ...item,
        amount,
        base_amount: baseAmount,
        total_tax_rate: totalTaxRate,
        tax_breakdown: breakdown,
        computed_tax_amount: itemTaxTotal,
        price_standard: toNumber(item?.price_standard),
        qty: toNumber(item?.qty),
      };
    });

    const subtotal = taxIncluded
      ? normalizedItems.reduce((sum, item) => sum + item.amount, 0)
      : normalizedItems.reduce((sum, item) => sum + item.base_amount, 0);

    const taxableBase = normalizedItems.reduce((sum, item) => sum + item.base_amount, 0);
    const taxBreakup  = Object.values(taxMap);
    const taxTotal    = taxBreakup.reduce((sum, tax) => sum + toNumber(tax.amount), 0);

    const deliverySettings = settings?.delivery_settings || {};
    const billingSettings  = settings?.billing_settings  || {};

    let deliveryCharge = 0;
    const sourceValue = orderSource?.value || defaultSource.value;
    const deliveryExemptSources = ["Dine-In", "Dine In", "Room Service", "Take Away", "Takeaway"];

    if (
      deliverySettings.enable_delivery_charges &&
      !deliveryExemptSources.includes(sourceValue) &&
      subtotal < toNumber(deliverySettings.free_above_order_value)
    ) {
      const sourceCharge = Array.isArray(deliverySettings.rate_card_specific_charges)
        ? deliverySettings.rate_card_specific_charges.find((charge) => charge.source === sourceValue)
        : null;
      deliveryCharge = toNumber(sourceCharge?.charge);
      if (
        !sourceCharge &&
        deliverySettings.calculation_mode === "flat" &&
        Array.isArray(deliverySettings.distance_based_charges) &&
        deliverySettings.distance_based_charges.length > 0
      ) {
        deliveryCharge = toNumber(deliverySettings.distance_based_charges[0]?.charge);
      }
    }

    const serviceCharge = billingSettings.enable_service_charges
      ? taxableBase * (toNumber(billingSettings.service_charge_percentage) / 100)
      : 0;

    let totalBeforeDiscount = taxIncluded
      ? subtotal + serviceCharge
      : subtotal + exclusiveAdditionalTax + serviceCharge;

    if (deliverySettings.include_delivery_in_bill_total) {
      totalBeforeDiscount += deliveryCharge;
    }

    const roundedBeforeDiscount =
      billingSettings.round_off_total_amount === "nearest_1"
        ? Math.round(totalBeforeDiscount)
        : totalBeforeDiscount;

    const discountAmount = roundedBeforeDiscount * (toNumber(discountPercentage) / 100);
    const grandTotal = isComplementary ? 0 : Math.max(0, roundedBeforeDiscount - discountAmount);

    return {
      subtotal, taxableBase, taxTotal, taxBreakup, serviceCharge, deliveryCharge,
      grandTotal, unroundedTotal: isComplementary ? 0 : totalBeforeDiscount,
      discountAmount, normalizedItems,
    };
  }, [safeKotItems, taxIncluded, isComplementary, orderSource, settings, discountPercentage, defaultSource.value]);

  const {
    subtotal, taxableBase, taxTotal, taxBreakup, serviceCharge, deliveryCharge,
    grandTotal, unroundedTotal, discountAmount, normalizedItems,
  } = calculation;

  const paidAmount   = partialPayments.reduce((sum, payment) => sum + toNumber(payment.amount), 0);
  const dueAmount    = Math.max(0, grandTotal - paidAmount);
  const hasDuePayment = partialPayments.some((payment) => payment.method === "Due");

  const paymentOptionsForPartial =
    safePaymentOptions.length > 0
      ? safePaymentOptions
      : [
          { value: "Cash", label: "Cash" },
          { value: "UPI",  label: "UPI"  },
          { value: "Card", label: "Card" },
          { value: "Due",  label: "Due"  },
        ];

  const partialMethodOptions = paymentOptionsForPartial.some((option) => option.value === "Due")
    ? paymentOptionsForPartial
    : [...paymentOptionsForPartial, { value: "Due", label: "Due" }];

  const resetState = () => {
    if (setKotItems) setKotItems([]);
    setCustomerName("");
    setCustomerPhone("");
    setPaymentMode(null);
    setOrderSource(defaultSource);
    setDiscountPercentage(0);
    setPartialPayments([]);
    setIsPartialPaymentEnabled(false);
    setCurrentPartialMethod("Cash");
    setCurrentPartialAmount("");
    setIsComplementary(false);
    setIsRoomCredit(false);
    setSelectedCreditRoom(entityType === "room" ? { value: entityId, label: entityId } : null);
    setIsFooterExpanded(false);
  };

  const buildKotsPayload = () => {
    const kotsMap = normalizedItems.reduce((acc, item) => {
      const kotId = item.kot_id || generateUUID();
      const existingKotItemId = item.kot_item_id || generateUUID();
      const entityKey = entityType === "room" ? item.room_id || entityId : item.table_id || entityId;

      if (!acc[kotId]) {
        acc[kotId] = {
          kot_id: kotId,
          [entityType === "room" ? "room_id" : "table_id"]: entityKey,
          items: [],
          kot_item_ids: [],
        };
      }

      acc[kotId].items.push({
        kot_item_id: existingKotItemId,
        menu_id: item.menu_id,
        name: item.name,
        unit: item.unit || "Unit",
        price_standard: item.price_standard,
        is_taxable: item.is_taxable,
        is_price_exclusive_tax: Boolean(item.is_price_exclusive_tax),
        tax_ids: item.tax_ids || [],
        tax_breakdown: item.tax_breakdown || [],
        total_tax_rate: item.total_tax_rate || 0,
        qty: item.qty,
        amount: item.amount,
        item_code: item.item_code,
        mrp: item.mrp,
      });

      acc[kotId].kot_item_ids.push(existingKotItemId);
      return acc;
    }, {});

    return {
      kots: Object.values(kotsMap).map((kot) => ({
        kot_id: kot.kot_id,
        [entityType === "room" ? "room_id" : "table_id"]:
          kot[entityType === "room" ? "room_id" : "table_id"],
        items: kot.items,
        kot_item_ids: kot.kot_item_ids,
        order_time: moment().format("YYYY-MM-DD HH:mm:ss"),
      })),
      kotIds: Object.keys(kotsMap),
    };
  };

  // ── printBill() REMOVED ───────────────────────────────────────────────────
  // Browser print replaced by kitchen agent printing via Socket.IO.
  // The backend emits bill:print after SaveTableServiceBills / SaveRoomServiceBills
  // commits. The agent receives the event, formats ESC/POS, sends to printer.
  // Staff no longer see a browser print dialog.

  const generateBillForEntity = async (billingEntityId) => {
    const { kots, kotIds } = buildKotsPayload();
    const duePartialAmount = partialPayments
      .filter((payment) => payment.method === "Due")
      .reduce((sum, payment) => sum + toNumber(payment.amount), 0);
    const cashPaidAmount = partialPayments
      .filter((payment) => payment.method !== "Due")
      .reduce((sum, payment) => sum + toNumber(payment.amount), 0);
    const hasDirectFullPayment = Boolean(paymentMode && !isPartialPaymentEnabled && !isRoomCredit);
    const shouldBeSettled =
      isComplementary || hasDirectFullPayment || (cashPaidAmount >= grandTotal && !hasDuePayment);

    const order = {
      order_id: `CSM-${Date.now().toString().slice(-4)}`,
      company_id: 1,
      customer_name: customerName || null,
      customer_phone: customerPhone || null,
      order_type: entityType === "room" ? "Room Service" : "Dine In",
      source: orderSource?.value || defaultSource.value,
      status: "Completed",
      is_settled: shouldBeSettled,
      billing_type: isRoomCredit ? "Room Credit" : "Direct",
      tax_amount: taxTotal,
      tax_breakup: taxBreakup.map((tax) => ({
        tax_id: tax.tax_id,
        tax_name: tax.tax_name,
        tax_rate: tax.tax_rate,
        amount: tax.amount,
      })),
      created_by: "user1",
      business_date: getBusinessDate(),
      kots,
      kot_ids: kotIds,
      payment_method: isComplementary
        ? "Complementary"
        : isRoomCredit
        ? "Room Credit"
        : paymentMode
        ? paymentMode.value
        : partialPayments.length > 0
        ? "Partial"
        : "",
      discount_percentage: toNumber(discountPercentage),
      partial_payments: partialPayments,
      is_complementary: isComplementary,
      delivery_charge: deliveryCharge,
      service_charge: serviceCharge,
      subtotal,
      taxable_base: taxableBase,
      grand_total: grandTotal,
      paid_amount: cashPaidAmount,
      due_amount: duePartialAmount,
      [entityType === "room" ? "room_id" : "table_id"]: billingEntityId,
    };

    const response = await axios.post(
      `${apiUrl}/pos/orders/orders/${entityType === "room" ? "rooms" : "tables"}`,
      order,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (
      response.status === 201 ||
      (response.data?.message && response.data.message.includes("saved successfully"))
    ) {
      // Bill saved — kitchen agent prints automatically via Socket.IO.
      // No browser print dialog. Show confirmation to staff.
     notify("success",
        isComplementary
          ? "Complimentary bill saved. Slip sent to kitchen printer."
          : "Bill saved successfully. Printing to kitchen printer..."
      );
      resetState();
      onClose?.();
      await fetchData?.();
      return;
    }

    throw new Error("Unexpected response from server");
  };

  const handleGenerateBill = async () => {
    if (!safeKotItems.length) {
      notify("error","No KOT items to bill. Please generate a KOT first.");
      return;
    }

    if (!apiUrl || !token) {
      notify("error","Billing API configuration is missing.");
      return;
    }

    if (!isComplementary && !isPartialPaymentEnabled && !isRoomCredit && !paymentMode) {
      notify("error","Please select a payment mode, add partial payments, or credit to room.");
      return;
    }

    if (isPartialPaymentEnabled) {
      if (partialPayments.length === 0) {
        notify("error","Please add at least one partial payment.");
        return;
      }
      const totalPartialAmount = partialPayments.reduce((sum, payment) => sum + toNumber(payment.amount), 0);
      if (Math.abs(totalPartialAmount - grandTotal) > 0.009) {
        notify("error","Partial payments must exactly cover the grand total. Add Due for the remaining amount.");
        return;
      }
    }

    setIsSaving(true);

    try {
      if (isRoomCredit && selectedCreditRoom) {
        const creditRoomId = selectedCreditRoom.value;

        if (entityType === "room" && creditRoomId !== entityId) {
          const result = await Swal.fire({
            title: "Room Mismatch",
            text: `You are charging this bill to ${creditRoomId}, which is different from the original room ${entityId}. Proceed?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, continue",
            cancelButtonText: "No",
          });
          if (!result.isConfirmed) return;
        }

        await generateBillForEntity(creditRoomId);
        return;
      }

      await generateBillForEntity(entityId);
    } catch (err) {
      console.error("Error saving bill:", err);
      notify("error","Failed to save bill: " + (err.response?.data?.error || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePartialPayment = () => {
    const nextValue = !isPartialPaymentEnabled;
    setIsPartialPaymentEnabled(nextValue);
    if (nextValue) {
      setPaymentMode(null);
      setIsRoomCredit(false);
    } else {
      setPartialPayments([]);
      setCurrentPartialMethod("Cash");
      setCurrentPartialAmount("");
    }
  };

  const handleAddPartialPayment = () => {
    const newAmount = toNumber(currentPartialAmount);
    if (newAmount <= 0) {
      notify("error","Please enter a valid amount.");
      return;
    }
    const currentPaid = partialPayments.reduce((sum, payment) => sum + toNumber(payment.amount), 0);
    if (currentPaid + newAmount > grandTotal + 0.009) {
      notify("error",`Amount exceeds total bill (${asMoney(grandTotal, currencyCode)}).`);
      return;
    }
    setPartialPayments((previous) => [...previous, { method: currentPartialMethod, amount: newAmount }]);
    setCurrentPartialAmount("");
  };

  const addDueForRemaining = () => {
    if (dueAmount <= 0) return;
    setPartialPayments((previous) => [...previous, { method: "Due", amount: dueAmount }]);
  };

  const handleRemovePartialPayment = (index) => {
    setPartialPayments((previous) => previous.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleRoomCreditToggle = (event) => {
    const checked = event.target.checked;
    setIsRoomCredit(checked);
    if (checked) {
      setPaymentMode(null);
      setIsPartialPaymentEnabled(false);
      setPartialPayments([]);
      setIsComplementary(false);
    }
  };

  const handleComplementaryToggle = (event) => {
    const checked = event.target.checked;
    setIsComplementary(checked);
    if (checked) {
      setPaymentMode(null);
      setIsPartialPaymentEnabled(false);
      setPartialPayments([]);
      setIsRoomCredit(false);
    }
  };

  if (!isOpen) return null;

  const entityLabel = entityType === "table" ? `Table ${tableNo || entityId}` : `Room ${entityId}`;
  const totalQty = normalizedItems.reduce((sum, item) => sum + toNumber(item.qty), 0);

  return (
    <aside className="ns-bill-panel" role="dialog" aria-modal="true" aria-label="Bill generation panel">
      <style>{billStyles}</style>

      {/* iframe REMOVED — printing handled by kitchen agent via Socket.IO */}

      <div className="ns-bill-header">
        <div className="ns-bill-title-block">
          <span className="ns-bill-title-icon">
            <Receipt size={22} />
          </span>
          <div>
            <div className="ns-bill-eyebrow">Billing Workspace</div>
            <h3>{entityLabel}</h3>
            <p>
              {safeKotItems.length} item{safeKotItems.length === 1 ? "" : "s"} · {kotCount} KOT
              {Number(kotCount) === 1 ? "" : "s"} · {totalQty} qty
            </p>
          </div>
        </div>

        <button type="button" className="ns-bill-close" onClick={onClose} aria-label="Close bill panel">
          <X size={19} />
        </button>
      </div>

      <div className="ns-bill-body">
        <section className="ns-bill-items-card">
          <div className="ns-bill-section-head">
            <div>
              <strong>KOT Items</strong>
              <span>Review billable items before settlement.</span>
            </div>
            <span className="ns-bill-soft-pill">{safeKotItems.length} rows</span>
          </div>

          <div className="ns-bill-items-list">
            {normalizedItems.length === 0 ? (
              <div className="ns-bill-empty">
                <CreditCard size={42} />
                <strong>No KOT items to bill</strong>
                <span>Generate a KOT first, then come back to billing.</span>
              </div>
            ) : (
              normalizedItems.map((item, index) => (
                <div key={`${item.kot_item_id || item.menu_id || item.name}-${index}`} className="ns-bill-item-row">
                  <div className="ns-bill-item-main">
                    <strong>{item.name || "Unnamed item"}</strong>
                    <span>KOT: {item.kot_number || item.kot_id || "-"}</span>
                  </div>
                  <div className="ns-bill-item-qty">x{item.qty || 0}</div>
                  <div className="ns-bill-item-amount">{asMoney(item.amount, currencyCode)}</div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="ns-bill-config-card">
          <div className="ns-bill-section-head compact">
            <div>
              <strong>Billing Options</strong>
              <span>Payment, source, tax, and settlement controls.</span>
            </div>
            <span className="ns-bill-guard">
              <Shield size={13} />
              Business Date {getBusinessDate()}
            </span>
          </div>

          <div className="ns-bill-toggle-grid">
            <Toggle
              id="ns-bill-tax-toggle"
              checked={taxIncluded}
              onChange={(event) => setTaxIncluded(event.target.checked)}
              label="Include Tax"
              icon={<Percent size={14} />}
            />
            <Toggle
              id="ns-bill-complementary-toggle"
              checked={isComplementary}
              onChange={handleComplementaryToggle}
              label="Complementary"
              icon={<Gift size={14} />}
            />
            <Toggle
              id="ns-bill-room-credit-toggle"
              checked={isRoomCredit}
              onChange={handleRoomCreditToggle}
              label="Credit to Room"
              icon={<Home size={14} />}
              disabled={isComplementary}
            />
          </div>

          <div className="ns-bill-form-grid">
            <label className="ns-bill-field">
              <span><User size={14} />Customer Name</span>
              <input
                type="text"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
                placeholder="Optional"
              />
            </label>

            <label className="ns-bill-field">
              <span><Phone size={14} />Customer Phone</span>
              <input
                type="text"
                value={customerPhone}
                onChange={(event) => setCustomerPhone(event.target.value)}
                placeholder="Optional"
              />
            </label>

            <label className="ns-bill-field">
              <span><CreditCard size={14} />Payment Method</span>
              <Select
                classNamePrefix="ns-bill-select"
                options={safePaymentOptions}
                value={paymentMode}
                onChange={(option) => {
                  setPaymentMode(option);
                  if (option) {
                    setIsPartialPaymentEnabled(false);
                    setPartialPayments([]);
                    setIsRoomCredit(false);
                  }
                }}
                placeholder="Select"
                styles={selectStyles}
                isDisabled={isComplementary || isPartialPaymentEnabled || isRoomCredit}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </label>

            <label className="ns-bill-field">
              <span><FileText size={14} />Order Source</span>
              <Select
                classNamePrefix="ns-bill-select"
                options={safeSourceOptions}
                value={orderSource}
                onChange={setOrderSource}
                placeholder="Select"
                styles={selectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </label>

            {isRoomCredit ? (
              <label className="ns-bill-field">
                <span><Home size={14} />Credit Room</span>
                <Select
                  classNamePrefix="ns-bill-select"
                  options={safeRoomOptions}
                  value={selectedCreditRoom}
                  onChange={setSelectedCreditRoom}
                  placeholder="Select room"
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </label>
            ) : null}
          </div>
        </section>

        <section
          className={`ns-bill-details-card ${isFooterExpanded ? "is-open" : "is-closed"}`}
          aria-hidden={!isFooterExpanded}
        >
          <div className="ns-bill-section-head compact">
            <div>
              <strong>Settlement Detail</strong>
              <span>Discount and partial payment handling.</span>
            </div>
            <button
              type="button"
              className="ns-bill-collapse-btn"
              onClick={() => setIsFooterExpanded(false)}
            >
              <ChevronDown size={16} />
            </button>
          </div>

          <div className="ns-bill-summary-lines">
            <div>
              <span>Subtotal</span>
              <strong>{asMoney(subtotal, currencyCode)}</strong>
            </div>

            {taxBreakup.map((tax) => (
              <div key={tax.tax_id}>
                <span>{tax.tax_name} ({toNumber(tax.tax_rate)}%)</span>
                <strong>{asMoney(tax.amount, currencyCode)}</strong>
              </div>
            ))}

            {taxBreakup.length > 0 ? (
              <div>
                <span>Total Tax</span>
                <strong>{asMoney(taxTotal, currencyCode)}</strong>
              </div>
            ) : null}

            {serviceCharge > 0 ? (
              <div>
                <span>Service Charge</span>
                <strong>{asMoney(serviceCharge, currencyCode)}</strong>
              </div>
            ) : null}

            {deliveryCharge > 0 ? (
              <div>
                <span>Delivery Charge</span>
                <strong>{asMoney(deliveryCharge, currencyCode)}</strong>
              </div>
            ) : null}

            <label className="ns-bill-discount-row">
              <span>Discount (%)</span>
              <input
                type="number"
                min="0"
                max="100"
                value={discountPercentage}
                onChange={(event) =>
                  setDiscountPercentage(Math.min(100, Math.max(0, toNumber(event.target.value))))
                }
              />
            </label>

            {discountAmount > 0 ? (
              <div>
                <span>Discount Amount</span>
                <strong>-{asMoney(discountAmount, currencyCode)}</strong>
              </div>
            ) : null}

            <div>
              <span>Original Total</span>
              <strong>{asMoney(unroundedTotal, currencyCode)}</strong>
            </div>
          </div>

          <label className="ns-bill-partial-toggle">
            <input
              type="checkbox"
              checked={isPartialPaymentEnabled}
              onChange={handleTogglePartialPayment}
              disabled={isComplementary || isRoomCredit}
            />
            <span>Partial Payment</span>
          </label>

          {isPartialPaymentEnabled ? (
            <div className="ns-bill-partial-box">
              <div className="ns-bill-partial-row">
                <Select
                  classNamePrefix="ns-bill-select"
                  options={partialMethodOptions}
                  value={partialMethodOptions.find((option) => option.value === currentPartialMethod)}
                  onChange={(option) => setCurrentPartialMethod(option?.value || "Cash")}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
                <input
                  type="number"
                  value={currentPartialAmount}
                  onChange={(event) => setCurrentPartialAmount(event.target.value)}
                  placeholder="Amount"
                  step="0.01"
                />
                <button type="button" onClick={handleAddPartialPayment}>
                  <Plus size={15} />
                  Add
                </button>
              </div>

              {dueAmount > 0 ? (
                <button type="button" className="ns-bill-add-due" onClick={addDueForRemaining}>
                  Add Due for remaining {asMoney(dueAmount, currencyCode)}
                </button>
              ) : null}

              <div className="ns-bill-partial-list">
                {partialPayments.map((payment, index) => (
                  <div key={`${payment.method}-${index}`}>
                    <span>
                      {payment.method}: <strong>{asMoney(payment.amount, currencyCode)}</strong>
                    </span>
                    <button type="button" onClick={() => handleRemovePartialPayment(index)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="ns-bill-due-line">
                <span>Paid</span>
                <strong>{asMoney(paidAmount, currencyCode)}</strong>
                <span>Balance</span>
                <strong>{asMoney(dueAmount, currencyCode)}</strong>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      <footer className="ns-bill-footer">
        <div className="ns-bill-total-strip">
          <button
            type="button"
            className="ns-bill-expand-btn"
            onClick={() => setIsFooterExpanded((previous) => !previous)}
            aria-label={isFooterExpanded ? "Collapse bill detail" : "Expand bill detail"}
          >
            {isFooterExpanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </button>

          <div>
            <span>Grand Total</span>
            <strong>{asMoney(grandTotal, currencyCode)}</strong>
          </div>

          {isComplementary ? <span className="ns-bill-footer-pill">Complementary</span> : null}
          {isRoomCredit    ? <span className="ns-bill-footer-pill">Room Credit</span>    : null}
        </div>

        <button
          type="button"
          className="ns-bill-generate-btn"
          onClick={handleGenerateBill}
          disabled={!safeKotItems.length || isSaving}
        >
          {isSaving ? (
            <>
              <span className="ns-bill-spinner" />
              Saving...
            </>
          ) : (
            <>
              <Printer size={18} />
              Generate Bill
            </>
          )}
        </button>
      </footer>
    </aside>
  );
};

const billStyles = `
  .ns-bill-select__menu-portal {
    z-index: 2147483647 !important;
  }

  .ns-bill-panel {
    position: fixed;
    top: 0;
    right: 0;
    width: clamp(520px, 48vw, 780px);
    max-width: 100vw;
    height: 100vh;
    z-index: 2147482500;
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.10), transparent 30%),
      #f8fafc;
    border-left: 1px solid rgba(15, 23, 42, 0.10);
    box-shadow: -28px 0 70px rgba(15, 23, 42, 0.22);
    display: flex;
    flex-direction: column;
    animation: nsBillIn 0.28s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @keyframes nsBillIn {
    from { opacity: 0; transform: translateX(32px); }
    to   { opacity: 1; transform: translateX(0);    }
  }

  .ns-bill-header {
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.16), transparent 34%),
      linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    padding: 15px 18px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: flex-start;
    flex: 0 0 auto;
  }

  .ns-bill-title-block {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    min-width: 0;
  }

  .ns-bill-title-icon {
    width: 45px;
    height: 45px;
    border-radius: 17px;
    background: linear-gradient(135deg, #1d4ed8, #38bdf8);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 14px 30px rgba(37, 99, 235, 0.22);
    flex: 0 0 auto;
  }

  .ns-bill-eyebrow {
    color: #2563eb;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.13em;
    font-weight: 950;
    margin-bottom: 4px;
  }

  .ns-bill-header h3 {
    color: #001f5b;
    font-size: 1.12rem;
    line-height: 1.18;
    font-weight: 950;
    margin: 0;
  }

  .ns-bill-header p {
    color: #64748b;
    font-size: 0.78rem;
    margin: 4px 0 0;
    font-weight: 800;
  }

  .ns-bill-close {
    width: 40px;
    height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(220, 38, 38, 0.14);
    background: #fef2f2;
    color: #b91c1c;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.16s ease, background 0.16s ease;
  }

  .ns-bill-close:hover {
    transform: translateY(-1px);
    background: #fee2e2;
  }

  .ns-bill-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 12px;
    display: grid;
    gap: 12px;
    scrollbar-width: thin;
    scrollbar-color: #93c5fd #e2e8f0;
  }

  .ns-bill-body::-webkit-scrollbar { width: 8px; }
  .ns-bill-body::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 999px; }
  .ns-bill-body::-webkit-scrollbar-thumb { background: #93c5fd; border-radius: 999px; }

  .ns-bill-items-card,
  .ns-bill-config-card,
  .ns-bill-details-card {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 20px;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
    padding: 13px;
  }

  .ns-bill-section-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 11px;
  }

  .ns-bill-section-head.compact { margin-bottom: 10px; }

  .ns-bill-section-head strong {
    display: block;
    color: #0f172a;
    font-size: 0.94rem;
    font-weight: 950;
    margin-bottom: 2px;
  }

  .ns-bill-section-head span {
    display: block;
    color: #64748b;
    font-size: 0.74rem;
    font-weight: 800;
  }

  .ns-bill-soft-pill,
  .ns-bill-footer-pill,
  .ns-bill-guard {
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
    padding: 5px 9px;
    font-size: 0.72rem;
    font-weight: 950;
    white-space: nowrap;
  }

  .ns-bill-guard {
    display: inline-flex !important;
    align-items: center;
    gap: 5px;
    background: #ecfdf5;
    color: #047857;
    border-color: #bbf7d0;
  }

  .ns-bill-items-list {
    max-height: 320px;
    overflow-y: auto;
    display: grid;
    gap: 8px;
    padding-right: 3px;
  }

  .ns-bill-item-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    gap: 10px;
    align-items: center;
    background: #f8fafc;
    border: 1px solid rgba(15, 23, 42, 0.06);
    border-radius: 15px;
    padding: 10px;
    transition: background 0.14s ease;
  }

  .ns-bill-item-row:hover { background: #f1f5f9; }

  .ns-bill-item-main { min-width: 0; }

  .ns-bill-item-main strong {
    display: block;
    color: #0f172a;
    font-size: 0.86rem;
    font-weight: 950;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ns-bill-item-main span {
    display: block;
    color: #94a3b8;
    font-size: 0.7rem;
    font-weight: 850;
    margin-top: 2px;
  }

  .ns-bill-item-qty {
    min-width: 34px;
    height: 28px;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.76rem;
    font-weight: 950;
  }

  .ns-bill-item-amount {
    color: #0f172a;
    font-size: 0.86rem;
    font-weight: 950;
    min-width: 82px;
    text-align: right;
  }

  .ns-bill-empty {
    min-height: 190px;
    border: 1px dashed rgba(100, 116, 139, 0.30);
    border-radius: 18px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }

  .ns-bill-empty strong { color: #0f172a; font-weight: 950; }

  .ns-bill-toggle-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 9px;
    margin-bottom: 12px;
  }

  .ns-bill-toggle {
    min-height: 42px;
    border-radius: 15px;
    background: #f8fafc;
    border: 1px solid rgba(15, 23, 42, 0.08);
    padding: 7px 9px;
    display: flex;
    align-items: center;
    gap: 9px;
    cursor: pointer;
    user-select: none;
    transition: background 0.18s ease, border-color 0.18s ease;
  }

  .ns-bill-toggle.disabled { opacity: 0.55; cursor: not-allowed; }

  .ns-bill-toggle.active {
    background: #ecfdf5;
    border-color: #bbf7d0;
  }

  .ns-bill-toggle input { display: none; }

  .ns-bill-toggle-track {
    width: 38px;
    height: 21px;
    border-radius: 999px;
    background: #cbd5e1;
    position: relative;
    flex: 0 0 auto;
    transition: background 0.2s ease;
  }

  .ns-bill-toggle-track::after {
    content: "";
    position: absolute;
    width: 17px;
    height: 17px;
    border-radius: 999px;
    left: 2px;
    top: 2px;
    background: #ffffff;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.22);
    transition: left 0.2s ease;
  }

  .ns-bill-toggle input:checked + .ns-bill-toggle-track { background: #16a34a; }
  .ns-bill-toggle input:checked + .ns-bill-toggle-track::after { left: 19px; }

  .ns-bill-toggle-copy {
    color: #0f172a;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.74rem;
    font-weight: 950;
  }

  .ns-bill-form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .ns-bill-field {
    min-width: 0;
    display: grid;
    gap: 5px;
  }

  .ns-bill-field > span {
    color: #334155;
    font-size: 0.74rem;
    font-weight: 950;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .ns-bill-field input {
    width: 100%;
    height: 42px;
    border-radius: 14px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    padding: 0 12px;
    color: #0f172a;
    font-size: 0.84rem;
    font-weight: 780;
    outline: none;
    transition: border-color 0.16s ease, box-shadow 0.16s ease;
  }

  .ns-bill-field input:focus,
  .ns-bill-discount-row input:focus,
  .ns-bill-partial-row input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }

  /* ── Smooth expand/collapse for settlement detail ── */
  .ns-bill-details-card {
    overflow: hidden;
    transition:
      max-height 0.38s cubic-bezier(0.4, 0, 0.2, 1),
      opacity    0.28s ease,
      transform  0.32s cubic-bezier(0.4, 0, 0.2, 1),
      padding    0.28s ease;
    will-change: max-height, opacity, transform;
    transform-origin: top;
  }

  .ns-bill-details-card.is-closed {
    max-height: 0;
    opacity: 0;
    transform: translateY(-8px) scaleY(0.96);
    visibility: hidden;
    pointer-events: none;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    border-color: transparent !important;
    box-shadow: none !important;
  }

  .ns-bill-details-card.is-open {
    max-height: 860px;
    opacity: 1;
    transform: translateY(0) scaleY(1);
    visibility: visible;
    pointer-events: auto;
    padding: 13px !important;
  }

  .ns-bill-collapse-btn {
    width: 34px;
    height: 34px;
    border-radius: 12px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: #f8fafc;
    color: #1d4ed8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.16s ease;
  }

  .ns-bill-collapse-btn:hover { background: #eff6ff; }

  .ns-bill-summary-lines {
    display: grid;
    gap: 7px;
  }

  .ns-bill-summary-lines > div,
  .ns-bill-discount-row {
    min-height: 32px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    border-bottom: 1px dashed rgba(15, 23, 42, 0.10);
    color: #64748b;
    font-size: 0.8rem;
    font-weight: 850;
  }

  .ns-bill-summary-lines strong { color: #0f172a; font-weight: 950; }

  .ns-bill-discount-row input {
    width: 74px;
    height: 32px;
    border-radius: 11px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 8px;
    text-align: right;
    font-weight: 850;
    outline: none;
  }

  .ns-bill-partial-toggle {
    margin: 12px 0 8px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #0f172a;
    font-weight: 950;
    font-size: 0.8rem;
    cursor: pointer;
  }

  .ns-bill-partial-box {
    background: #f8fafc;
    border: 1px solid rgba(15, 23, 42, 0.07);
    border-radius: 16px;
    padding: 10px;
    display: grid;
    gap: 9px;
    animation: nsBillIn 0.22s ease both;
  }

  .ns-bill-partial-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 110px auto;
    gap: 8px;
    align-items: center;
  }

  .ns-bill-partial-row input {
    height: 42px;
    border-radius: 14px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 10px;
    font-weight: 850;
    outline: none;
  }

  .ns-bill-partial-row button,
  .ns-bill-add-due {
    height: 42px;
    border-radius: 14px;
    border: 0;
    background: #1d4ed8;
    color: #ffffff;
    font-size: 0.78rem;
    font-weight: 950;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 12px;
    cursor: pointer;
    transition: background 0.16s ease, transform 0.12s ease;
  }

  .ns-bill-partial-row button:hover { background: #1e40af; transform: translateY(-1px); }
  .ns-bill-add-due { width: fit-content; background: #f59e0b; }
  .ns-bill-add-due:hover { background: #d97706; }

  .ns-bill-partial-list { display: grid; gap: 6px; }

  .ns-bill-partial-list > div {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.07);
    border-radius: 13px;
    padding: 8px 9px;
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
    color: #475569;
    font-size: 0.78rem;
    font-weight: 850;
    animation: nsBillIn 0.18s ease both;
  }

  .ns-bill-partial-list button {
    width: 30px;
    height: 30px;
    border-radius: 11px;
    border: 0;
    background: #fee2e2;
    color: #b91c1c;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.14s ease;
  }

  .ns-bill-partial-list button:hover { background: #fecaca; }

  .ns-bill-due-line {
    display: grid;
    grid-template-columns: auto 1fr auto 1fr;
    gap: 7px;
    color: #64748b;
    font-size: 0.76rem;
    font-weight: 850;
  }

  .ns-bill-due-line strong { color: #0f172a; }

  .ns-bill-footer {
    flex: 0 0 auto;
    background: #ffffff;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
    padding: 11px 12px;
    display: grid;
    gap: 10px;
    box-shadow: 0 -12px 30px rgba(15, 23, 42, 0.06);
  }

  .ns-bill-total-strip {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .ns-bill-expand-btn {
    width: 38px;
    height: 38px;
    border-radius: 14px;
    border: 0;
    background: #eff6ff;
    color: #1d4ed8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.16s ease, transform 0.16s ease;
  }

  .ns-bill-expand-btn:hover { background: #dbeafe; transform: translateY(-1px); }

  .ns-bill-total-strip > div { flex: 1 1 auto; min-width: 0; }

  .ns-bill-total-strip span {
    display: block;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 900;
  }

  .ns-bill-total-strip strong {
    display: block;
    color: #16a34a;
    font-size: 1.18rem;
    font-weight: 950;
    line-height: 1.15;
  }

  .ns-bill-generate-btn {
    width: 100%;
    min-height: 46px;
    border-radius: 16px;
    border: 0;
    background: linear-gradient(135deg, #16a34a, #22c55e);
    color: #ffffff;
    font-size: 0.92rem;
    font-weight: 950;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    cursor: pointer;
    box-shadow: 0 14px 30px rgba(22, 163, 74, 0.22);
    transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
  }

  .ns-bill-generate-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 18px 38px rgba(22, 163, 74, 0.28);
  }

  .ns-bill-generate-btn:disabled {
    opacity: 0.62;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  /* ── Saving spinner ── */
  .ns-bill-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #ffffff;
    border-radius: 999px;
    animation: ns-bill-spin 0.7s linear infinite;
    flex: 0 0 auto;
  }

  @keyframes ns-bill-spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 920px) {
    .ns-bill-select__menu-portal { z-index: 2147483647 !important; }
    .ns-bill-panel { width: 100vw; }
    .ns-bill-toggle-grid,
    .ns-bill-form-grid { grid-template-columns: 1fr; }
    .ns-bill-partial-row { grid-template-columns: 1fr; }
  }
`;

export default BillGeneration;